import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GoogleReview {
  id: string;
  source: 'google' | 'manual';
  external_id: string | null;
  author_name: string;
  author_photo_url: string | null;
  rating: number;
  text: string | null;
  review_date: string;
  language: string | null;
  is_published: boolean;
  display_order: number | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoogleReviewInput {
  author_name: string;
  author_photo_url?: string | null;
  rating: number;
  text?: string | null;
  review_date: string;
  is_published?: boolean;
}

// Public hook - only published
export function usePublishedGoogleReviews() {
  return useQuery({
    queryKey: ['google_reviews', 'published'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('google_reviews')
        .select('*')
        .eq('is_published', true)
        .order('review_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as GoogleReview[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Admin hook - all reviews
export function useAllGoogleReviews() {
  return useQuery({
    queryKey: ['google_reviews', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('google_reviews')
        .select('*')
        .order('review_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as GoogleReview[];
    },
  });
}

export function useTogglePublished() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_published }: { id: string; is_published: boolean }) => {
      const { error } = await supabase
        .from('google_reviews')
        .update({ is_published })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['google_reviews'] });
    },
  });
}

export function useDeleteReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('google_reviews').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google_reviews'] }),
  });
}

export function useSyncGoogleReviews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-google-reviews', {
        body: {},
      });
      if (error) {
        let detail = error.message;
        try {
          const ctx: any = (error as any).context;
          if (ctx?.json) {
            const j = await ctx.json();
            if (j?.error) detail = typeof j.error === 'string' ? j.error : JSON.stringify(j.error);
          } else if (ctx?.text) {
            const t = await ctx.text();
            if (t) detail = t;
          }
        } catch {}
        throw new Error(detail);
      }
      if (data?.error) throw new Error(data.error);
      return data as { success: boolean; synced: number; total_rating: number | null; total_count: number | null };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google_reviews'] }),
  });
}

export function useGoogleReviewSettings() {
  return useQuery({
    queryKey: ['settings', 'google_reviews'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['google_place_id', 'google_profile_url', 'google_rating_average', 'google_rating_count']);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((r: any) => { map[r.key] = r.value ?? ''; });
      const avg = parseFloat(map.google_rating_average ?? '');
      const cnt = parseInt(map.google_rating_count ?? '', 10);
      return {
        google_place_id: map.google_place_id ?? '',
        google_profile_url: map.google_profile_url ?? '',
        google_rating_average: Number.isFinite(avg) ? avg : null,
        google_rating_count: Number.isFinite(cnt) ? cnt : null,
      };
    },
  });
}

export function useUpdateGoogleReviewSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase.from('settings').update({ value }).eq('key', key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'google_reviews'] }),
  });
}
